import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppApis } from '@app/core/APIs/app-apis';
import { map, throwError, type Observable } from 'rxjs';
import type { WorkflowDeadlineBinding } from '@app/shared/workflow-deadlines/workflow-deadline.models';
import {
  workflowTimerListV2,
  workflowTimerV2,
  type WorkflowTimerReadV2,
} from './workflow-timers-v2.models';
import type {
  WorkflowTimerChangeRequest,
  WorkflowTimerRead,
} from './workflow-timers.models';

@Injectable({ providedIn: 'root' })
export class WorkflowTimersService {
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private readonly headers = new HttpHeaders({
    'skip-error-interceptor': 'yes',
    'skip-loading-interceptor': 'yes',
  });
  read(workflowId: number, occurrenceId: number) {
    return this.http.get<WorkflowTimerRead[]>(
      `${this.apis.core.baseUrl}/api/platform/workflows/${workflowId}/timers`,
      {
        headers: this.headers,
        withCredentials: true,
        params: { occurrenceId },
      },
    );
  }
  change(
    workflowId: number,
    request: WorkflowTimerChangeRequest,
    idempotencyKey: string,
  ) {
    return this.http.post<WorkflowTimerRead>(
      `${this.apis.core.baseUrl}/api/platform/workflows/${workflowId}/timers/change`,
      request,
      {
        headers: this.headers
          .set('If-Match', request.expectedETag)
          .set('Idempotency-Key', idempotencyKey),
        withCredentials: true,
      },
    );
  }

  readV2(
    workflowId: number,
    occurrenceId: number,
    binding?: Partial<WorkflowDeadlineBinding>,
  ): Observable<WorkflowTimerReadV2[]> {
    const source = this.sourceV2(workflowId, occurrenceId, binding);
    if (!source) return throwError(() => this.unavailableV2());
    return this.http
      .get<unknown>(
        `${this.apis.core.baseUrl}/api/platform/workflows/${workflowId}/timers/v2`,
        {
          headers: this.headers,
          withCredentials: true,
          params: { occurrenceId },
        },
      )
      .pipe(
        map((value) => {
          if (!workflowTimerListV2(value, source)) throw this.unavailableV2();
          return value;
        }),
      );
  }

  changeV2(
    workflowId: number,
    request: WorkflowTimerChangeRequest,
    idempotencyKey: string,
    binding?: Partial<WorkflowDeadlineBinding>,
  ): Observable<WorkflowTimerReadV2> {
    const source = this.sourceV2(workflowId, request.occurrenceId, binding);
    if (
      !source ||
      !['pause', 'resume', 'reset'].includes(request.operation) ||
      typeof request.timerId !== 'string' ||
      !Number.isSafeInteger(request.expectedRevision) ||
      request.expectedRevision < 1 ||
      (binding?.sourceId != null && binding.sourceId !== request.timerId) ||
      (binding?.sourceRevision != null &&
        binding.sourceRevision !== request.expectedRevision)
    )
      return throwError(() => this.unavailableV2());
    // A reset legitimately creates a new source. Only immutable owner/publication pins are universal response bindings.
    const owner: WorkflowDeadlineBinding = {
      workflowInstanceId: source.workflowInstanceId,
      occurrenceId: source.occurrenceId,
      definitionVersionId: source.definitionVersionId,
      configurationVersionId: source.configurationVersionId,
      definitionHash: source.definitionHash,
      isCurrent: true,
    };
    return this.http
      .post<unknown>(
        `${this.apis.core.baseUrl}/api/platform/workflows/${workflowId}/timers/v2/change`,
        request,
        {
          headers: this.headers
            .set('If-Match', request.expectedETag)
            .set('Idempotency-Key', idempotencyKey),
          withCredentials: true,
        },
      )
      .pipe(
        map((value) => {
          if (!workflowTimerV2(value, owner)) throw this.unavailableV2();
          if (
            value.state !==
            (request.operation === 'pause' ? 'paused' : 'active')
          )
            throw this.unavailableV2();
          const reset = request.operation === 'reset';
          if (
            reset
              ? value.id.toLowerCase() === request.timerId.toLowerCase() ||
                value.revision !== 1 ||
                (binding?.generation != null &&
                  value.generation !== binding.generation + 1)
              : value.id.toLowerCase() !== request.timerId.toLowerCase() ||
                value.revision <= request.expectedRevision ||
                (binding?.generation != null &&
                  value.generation !== binding.generation)
          )
            throw this.unavailableV2();
          return value;
        }),
      );
  }

  private sourceV2(
    workflowId: number,
    occurrenceId: number,
    binding?: Partial<WorkflowDeadlineBinding>,
  ): WorkflowDeadlineBinding | null {
    if (
      !Number.isSafeInteger(workflowId) ||
      workflowId < 1 ||
      !Number.isSafeInteger(occurrenceId) ||
      occurrenceId < 1 ||
      (binding?.workflowInstanceId != null &&
        binding.workflowInstanceId !== workflowId) ||
      (binding?.occurrenceId != null && binding.occurrenceId !== occurrenceId)
    )
      return null;
    return { ...binding, workflowInstanceId: workflowId, occurrenceId };
  }

  private unavailableV2(): Error {
    return new Error(
      'The selected timer response is unavailable or incompatible. Refresh the exact workflow and visit before continuing.',
    );
  }
}
